import React, { useMemo, useState } from "react";
import { WarningCircle, Warning, Info, ClockCounterClockwise, Broom } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const levelStyle = {
  CRITICAL: { color: "#EF4444", bg: "bg-red-500/10", border: "border-red-500/30", icon: WarningCircle },
  HIGH: { color: "#F59E0B", bg: "bg-amber-500/10", border: "border-amber-500/30", icon: Warning },
  MEDIUM: { color: "#3EB6F0", bg: "bg-sky-500/10", border: "border-sky-500/30", icon: Info },
};

export default function AlertsHistoryPanel({ sessionAlerts, onClear }) {
  const [level, setLevel] = useState("all");
  const [meter, setMeter] = useState("all");

  const meters = useMemo(() => {
    const s = new Set();
    sessionAlerts.forEach((a) => { if (a.meter_id) s.add(a.meter_id); });
    return Array.from(s).sort();
  }, [sessionAlerts]);

  const filtered = useMemo(() => {
    return sessionAlerts.filter((a) =>
      (level === "all" || a.livello === level) && (meter === "all" || a.meter_id === meter)
    );
  }, [sessionAlerts, level, meter]);

  return (
    <div className="bg-[#111827] border border-slate-800 rounded-lg" data-testid="alerts-history-panel">
      <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={20} weight="duotone" className="text-[#3EB6F0]" />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Sessione corrente</div>
            <div className="font-heading text-lg text-white">Storico Allarmi ({sessionAlerts.length})</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-32 h-8 bg-[#0a0f1c] border-slate-800 font-mono text-xs" data-testid="history-filter-level">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-800">
              <SelectItem value="all" className="text-xs font-mono">Tutti livelli</SelectItem>
              <SelectItem value="CRITICAL" className="text-xs font-mono">CRITICAL</SelectItem>
              <SelectItem value="HIGH" className="text-xs font-mono">HIGH</SelectItem>
              <SelectItem value="MEDIUM" className="text-xs font-mono">MEDIUM</SelectItem>
            </SelectContent>
          </Select>
          <Select value={meter} onValueChange={setMeter}>
            <SelectTrigger className="w-36 h-8 bg-[#0a0f1c] border-slate-800 font-mono text-xs" data-testid="history-filter-meter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-slate-800 max-h-64">
              <SelectItem value="all" className="text-xs font-mono">Tutti meter</SelectItem>
              {meters.map((m) => (
                <SelectItem key={m} value={m} className="text-xs font-mono">{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={onClear} disabled={sessionAlerts.length === 0} data-testid="history-clear-button"
            className="border-slate-700 hover:bg-slate-800 gap-1 h-8">
            <Broom size={14} /> Pulisci
          </Button>
        </div>
      </div>
      <div className="p-3 max-h-[300px] overflow-y-auto space-y-2">
        {filtered.length === 0 && (
          <div className="text-center py-8 text-xs text-slate-500 font-mono">
            {sessionAlerts.length === 0 ? "Nessun allarme ricevuto in sessione" : "Nessun allarme con questi filtri"}
          </div>
        )}
        {filtered.map((a, i) => {
          const s = levelStyle[a.livello] || levelStyle.MEDIUM;
          const Icon = s.icon;
          return (
            <div key={`${a.timestamp}-${i}`} className={`border ${s.border} ${s.bg} rounded-md p-3`} data-testid={`history-item-${i}`}>
              <div className="flex items-start gap-3">
                <Icon size={16} weight="fill" style={{ color: s.color }} className="mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: s.color, background: s.color + "22" }}>{a.livello}</span>
                    {a.meter_id && <span className="text-[10px] font-mono text-[#3EB6F0]">{a.meter_id}</span>}
                    <span className="text-[10px] font-mono text-slate-500">
                      {new Date(a.timestamp).toLocaleTimeString("it-IT")}
                    </span>
                  </div>
                  <div className="text-sm text-slate-200 leading-snug">{a.messaggio}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
